// Browser-side image compression so big phone/WhatsApp photos (5+ MB) fit ingress
// body size limits. Uses an HTMLCanvasElement — zero external dependencies.
// Output: max 1600px on the longest side, JPEG quality 0.85 (~150-300 KB).

const MAX_DIMENSION = 1600;
const JPEG_QUALITY = 0.85;

export async function compressImageFile(file) {
  // Non-images are returned as-is
  if (!file || !file.type || !file.type.startsWith('image/')) return file;

  // Animated GIFs: leave untouched if under 3 MB; canvas would freeze the animation
  if (file.type === 'image/gif' && file.size < 3 * 1024 * 1024) return file;

  // Try to load the image into memory
  let bitmap;
  try {
    // createImageBitmap is fast and supports EXIF auto-orient on modern browsers
    bitmap = await createImageBitmap(file, { imageOrientation: 'from-image' });
  } catch {
    // Fallback for older browsers
    bitmap = await new Promise((resolve, reject) => {
      const img = new Image();
      img.onload = () => resolve(img);
      img.onerror = reject;
      img.src = URL.createObjectURL(file);
    });
  }

  const width = bitmap.width || bitmap.naturalWidth;
  const height = bitmap.height || bitmap.naturalHeight;
  if (!width || !height) return file;

  let targetW = width;
  let targetH = height;
  if (Math.max(width, height) > MAX_DIMENSION) {
    const ratio = MAX_DIMENSION / Math.max(width, height);
    targetW = Math.round(width * ratio);
    targetH = Math.round(height * ratio);
  }

  const canvas = document.createElement('canvas');
  canvas.width = targetW;
  canvas.height = targetH;
  const ctx = canvas.getContext('2d');
  // Solid white background so transparent areas don't become black on JPEG conversion
  ctx.fillStyle = '#ffffff';
  ctx.fillRect(0, 0, targetW, targetH);
  ctx.drawImage(bitmap, 0, 0, targetW, targetH);

  const blob = await new Promise(resolve => canvas.toBlob(resolve, 'image/jpeg', JPEG_QUALITY));
  if (!blob) return file;

  // If compression made it larger (rare), keep original
  if (blob.size >= file.size) return file;

  const name = (file.name || 'foto').replace(/\.[^.]+$/, '') + '.jpg';
  return new File([blob], name, { type: 'image/jpeg', lastModified: Date.now() });
}

// Compress a File/Blob and return base64-encoded JPEG (no data URL prefix).
// Used by the Tarefas module to upload foto-prova via JSON.
export async function compressImageToBase64(file, { maxDim = MAX_DIMENSION, quality = JPEG_QUALITY } = {}) {
  if (!file) throw new Error('Sem ficheiro');
  let bitmap;
  try {
    bitmap = await createImageBitmap(file, { imageOrientation: 'from-image' });
  } catch {
    bitmap = await new Promise((resolve, reject) => {
      const img = new Image();
      img.onload = () => resolve(img);
      img.onerror = reject;
      img.src = URL.createObjectURL(file);
    });
  }
  const w = bitmap.width || bitmap.naturalWidth;
  const h = bitmap.height || bitmap.naturalHeight;
  let tW = w, tH = h;
  if (Math.max(w, h) > maxDim) {
    const r = maxDim / Math.max(w, h);
    tW = Math.round(w * r); tH = Math.round(h * r);
  }
  const canvas = document.createElement('canvas');
  canvas.width = tW; canvas.height = tH;
  const ctx = canvas.getContext('2d');
  ctx.fillStyle = '#ffffff'; ctx.fillRect(0, 0, tW, tH);
  ctx.drawImage(bitmap, 0, 0, tW, tH);
  const dataUrl = canvas.toDataURL('image/jpeg', quality);
  // Strip "data:image/jpeg;base64,"
  return dataUrl.split(',', 2)[1];
}
